// GENERATED from functions/src/transportValidation.ts. Do not edit here.
/* eslint-disable max-len, require-jsdoc */
// Canonical, Firebase-free validation shared with the UI by sync-transport-validation.cjs.
export type TransportRecord = {
  id?: string; driverId?: unknown; driverName?: unknown; dayType?: unknown;
  startDate?: unknown; endDate?: unknown; reportStartDate?: unknown; reportEndDate?: unknown;
  workInStore?: unknown; isOwn?: unknown; truck?: {id?: unknown; plate?: unknown};
  startKm?: unknown; endKm?: unknown;
};
export type TransportMode = "plan" | "report";
export const PLAN_FIELDS = ["driverId", "driverName", "dayType", "startDate", "endDate", "workInStore", "eventNumber", "isOwn", "truck", "startPoint", "endPoint", "planNotes"] as const;
export const REPORT_FIELDS = ["reportStartDate", "reportEndDate", "startKm", "endKm", "reportNotes"] as const;
const dateParts = new Intl.DateTimeFormat("en-GB", {timeZone: "Europe/Sofia", year: "numeric", month: "2-digit", day: "2-digit"});

// No Date.parse on arbitrary numbers/text: JSON timestamps and ISO date strings only.
export function transportMillis(value: unknown): number | null {
  let result: number;
  if (value instanceof Date) result = value.getTime();
  else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) {
    const [year, month, day, hour, minute, second] = value.slice(0, 19).split(/[-T:]/).map(Number);
    const check = new Date(0);
    check.setUTCFullYear(year, month - 1, day);
    if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day || hour > 23 || minute > 59 || second > 59) return null;
    result = Date.parse(value);
  } else if (value && typeof value === "object" && "seconds" in value) {
    const source = value as {seconds: unknown; nanoseconds?: unknown};
    const nanos = source.nanoseconds ?? 0;
    if (typeof source.seconds !== "number" || !Number.isInteger(source.seconds) ||
      typeof nanos !== "number" || !Number.isInteger(nanos) || nanos < 0 || nanos >= 1e9) return null;
    result = source.seconds * 1000 + nanos / 1e6;
  } else return null;
  return Number.isFinite(result) && result >= -62135596800000 && result <= 253402300799999 ? result : null;
}

export function normalizedPlate(value: unknown): string {
  return typeof value === "string" ? value.replace(/\s+/gu, "").toUpperCase() : "";
}

function sofiaDay(value: number): number {
  const parts = dateParts.formatToParts(new Date(value));
  const part = (name: string) => Number(parts.find((item) => item.type === name)?.value);
  const date = new Date(0);
  date.setUTCFullYear(part("year"), part("month") - 1, part("day"));
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

// Binary search for Sofia midnight avoids fixed UTC offsets, including DST days.
function dayStart(value: number): number {
  const day = sofiaDay(value);
  let low = day - 15 * 3600000;
  let high = day + 15 * 3600000;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (sofiaDay(middle) < day) low = middle + 1;
    else high = middle;
  }
  return low;
}

export function transportPeriod(value: TransportRecord, mode: TransportMode): [number, number] | null {
  const start = transportMillis(mode === "plan" ? value.startDate : value.reportStartDate);
  const end = transportMillis(mode === "plan" ? value.endDate : value.reportEndDate);
  if (start === null || end === null || end < start) return null;
  if (mode === "plan" && value.dayType && value.dayType !== "WORKDAY") {
    // Last millisecond of the final local day; persisted user dates stay unchanged.
    return [dayStart(start), dayStart(dayStart(end) + 36 * 3600000) - 1];
  }
  return [start, end];
}

export function periodsOverlap(a: [number, number], b: [number, number]): boolean {
  if (a[0] === a[1]) return b[0] <= a[0] && a[0] <= b[1];
  if (b[0] === b[1]) return a[0] <= b[0] && b[0] <= a[1];
  return a[0] < b[1] && b[0] < a[1];
}

export function vehicleKey(value: TransportRecord): string | null {
  if (value.workInStore === true || (value.dayType && value.dayType !== "WORKDAY")) return null;
  if (value.isOwn !== false) return typeof value.truck?.id === "string" && value.truck.id ? `own:${value.truck.id}` : null;
  const plate = normalizedPlate(value.truck?.plate);
  return plate ? `external:${plate}` : null;
}

export function transportErrors(value: TransportRecord, mode: TransportMode): string[] {
  const errors: string[] = [];
  if (typeof value.driverId !== "string" || !value.driverId.trim()) errors.push("Не е избран шофьор.");
  const start = transportMillis(mode === "plan" ? value.startDate : value.reportStartDate);
  const end = transportMillis(mode === "plan" ? value.endDate : value.reportEndDate);
  if (start === null || end === null) errors.push(mode === "plan" ? "Въведете начален и краен час." : "За отчет са нужни начален и краен час.");
  else if (end < start) errors.push("Крайният час не може да е преди началния.");
  if (value.dayType !== undefined && !["WORKDAY", "HOLIDAY", "DAYOFF", "SICK"].includes(String(value.dayType))) errors.push("Невалиден вид ден.");
  if (mode === "report") {
    if (value.dayType && value.dayType !== "WORKDAY") errors.push("Не може да се отчита транспорт за отпуск или отсъствие.");
    for (const key of ["startKm", "endKm"] as const) {
      const km = value[key];
      if (km !== null && km !== undefined && (typeof km !== "number" || !Number.isSafeInteger(km) || km < 0)) errors.push("Километражът трябва да е неотрицателно цяло число.");
    }
    if (typeof value.startKm === "number" && typeof value.endKm === "number" && value.endKm > 0 && value.endKm < value.startKm) errors.push("Крайният километраж не може да е по-малък от началния.");
  }
  return Array.from(new Set(errors));
}

export function transportConflicts(candidate: TransportRecord, existing: TransportRecord[], mode: TransportMode): string[] {
  const period = transportPeriod(candidate, mode);
  if (!period) return [];
  const vehicle = vehicleKey(candidate);
  const format = (ms: number) => new Date(ms).toLocaleString("bg-BG", {timeZone: "Europe/Sofia", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"});
  const errors: string[] = [];
  for (const current of existing) {
    if (candidate.id && candidate.id === current.id) continue;
    const other = transportPeriod(current, mode);
    if (!other || !periodsOverlap(period, other)) continue;
    const label = `${format(other[0])} – ${format(other[1])}`;
    if (candidate.driverId && candidate.driverId === current.driverId) errors.push(`Шофьорът „${candidate.driverName || current.driverName || "избраният шофьор"}“ е зает в периода ${label}.`);
    if (vehicle && vehicle === vehicleKey(current)) errors.push(`МПС „${candidate.truck?.plate || current.truck?.plate || "избраното МПС"}“ е заето в периода ${label}.`);
  }
  return Array.from(new Set(errors));
}
